import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { useT } from '../i18n';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from './ui/dialog';
import ConfirmDialog from './ConfirmDialog';
import { Key, Plus, Trash2, Copy, BookOpen } from 'lucide-react';
import XpnsrPageHeader from './XpnsrPageHeader';
import XpnsrEmpty from './XpnsrEmpty';
import { BACKEND_URL } from '../runtimeConfig';

const SCOPES = ['read', 'write', 'admin'];

export default function ApiKeys() {
  const { t } = useT();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState(['read']);
  const [freshKey, setFreshKey] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api-keys`);
      setKeys(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return toast.error(t('Name is required'));
    try {
      const r = await axios.post(`${API}/api-keys`, { name: name.trim(), scopes });
      setFreshKey(r.data);
      setName(''); setScopes(['read']); setOpen(false);
      toast.success(t('Key created'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Action failed'));
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/api-keys/${id}`);
      setDeleteTarget(null);
      toast.success(t('Key deleted'));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Failed to delete key'));
    }
  };

  const copy = (s) => { navigator.clipboard.writeText(s); toast.success(t('Copied')); };
  const toggleScope = (s) => setScopes(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

  const base = BACKEND_URL;

  return (
    <div className="px-8 py-10 max-w-5xl">
      <XpnsrPageHeader
        kicker={t('Public API')}
        title={t('API keys')}
        subtitle={
          <>
            {t('Use API keys to call the public /v1 REST API. Pass the key as')}{' '}
            <code className="font-mono text-foreground bg-surface-2 px-1 rounded">Authorization: Bearer &lt;key&gt;</code>
            {' '}{t('or')}{' '}
            <code className="font-mono text-foreground bg-surface-2 px-1 rounded">X-Api-Key</code>.
          </>
        }
        actions={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setName(''); setScopes(['read']); } }}>
          <DialogTrigger asChild>
            <Button><Plus className="w-4 h-4 mr-1" /> {t('New key')}</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t('New API key')}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>{t('Name')}</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('zapier integration')} />
              </div>
              <div>
                <Label className="mb-2 block">{t('Scopes')}</Label>
                <div className="flex gap-2">
                  {SCOPES.map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => toggleScope(s)}
                      className={`px-3 py-1 rounded border text-sm font-mono ${scopes.includes(s) ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'}`}
                    >{s}</button>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button onClick={create}>{t('Create')}</Button>
              </DialogFooter>
            </div>
          </DialogContent>
          </Dialog>
        }
      />
      <div className="mb-6" />

      {freshKey && (
        <Card className="mb-6 border-primary/40">
          <CardHeader><CardTitle className="text-primary">{t('Copy your key now — it will not be shown again')}</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <code className="flex-1 font-mono text-sm break-all bg-surface-2 p-3 rounded">{freshKey.key}</code>
              <Button variant="outline" size="sm" onClick={() => copy(freshKey.key)}>
                <Copy className="w-4 h-4 mr-1" /> {t('Copy')}
              </Button>
            </div>
            <Button className="mt-3" variant="outline" size="sm" onClick={() => setFreshKey(null)}>{t('Dismiss')}</Button>
          </CardContent>
        </Card>
      )}

      <Card className="mb-6 bg-surface-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><BookOpen className="w-4 h-4" /> {t('Quick start')}</CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="font-mono text-xs overflow-x-auto">
{`curl ${base}/v1/campaigns \\
  -H "Authorization: Bearer <your-key>"

curl ${base}/v1/conversions \\
  -X POST \\
  -H "Authorization: Bearer <your-key>" \\
  -H "Content-Type: application/json" \\
  -d '{"click_id":"abc123","revenue":9.99,"currency":"USD"}'`}
          </pre>
          <p className="text-xs text-muted-foreground mt-3">{t('Docs')}: <a href={`${base}/docs`} target="_blank" rel="noreferrer" className="text-primary hover:underline">{base}/docs</a></p>
        </CardContent>
      </Card>

      {loading ? (
        <p className="text-muted-foreground">{t('Loading…')}</p>
      ) : keys.length === 0 ? (
        <Card>
          <CardContent style={{ padding: 0 }}>
            <XpnsrEmpty
              icon={Key}
              title={t('No API keys yet')}
              subtitle={t('Generate a key to call the public REST API.')}
              cta={
                <button
                  type="button"
                  className="xp-btn xp-btn-primary"
                  onClick={() => setOpen(true)}
                  data-testid="empty-create-api-key"
                >
                  <Plus style={{ width: 12, height: 12 }} /> {t('Create key')}
                </button>
              }
            />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {keys.map(k => (
            <Card key={k.id}>
              <CardContent className="py-4 flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{k.name}</span>
                    {(k.scopes || []).map(s => (
                      <Badge key={s} className="bg-primary/15 text-primary border border-primary/30">{s}</Badge>
                    ))}
                  </div>
                  <div className="font-mono text-xs text-muted-foreground mt-1">
                    {k.prefix}…  ·  {k.last_used_at ? `${t('last used')} ${new Date(k.last_used_at).toLocaleString()}` : t('never used')}
                  </div>
                </div>
                <Button variant="outline" size="sm" aria-label={t('Delete API key')} title={t('Delete API key')} onClick={() => setDeleteTarget(k)}><Trash2 className="w-4 h-4" /></Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title={t('Delete API key')}
        description={t('This will permanently revoke this API key. Any integrations using it will stop working.')}
        confirmLabel={t('Delete')}
        onConfirm={() => remove(deleteTarget?.id)}
      />
    </div>
  );
}
